import { useEffect, useState, useCallback } from 'react';
import { useMediaQuery } from '@mui/material';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Spacing, Colors, Palette, Dimensions } from '@/shared/theme';
import { BorisScreener } from '@/shared/borisScreenerTheme';
import { Title, Text } from '@/shared/ui';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useAppFlow, STEP_KEYS } from '@/hooks/useAppFlow';
import { useRegistration } from '@/context/RegistrationContext';
import { useApiError } from '@/context/ApiErrorContext';
import { TOSModal } from '@/components/modals/TOSModal';
import { PrivacyPolicyModal } from '@/components/modals/PrivacyPolicyModal';
import { apiClient } from '@/services/api';
import { env } from '@/env';
import { VIEW_WELCOME, START_WEBREG, NEED_HELP, ENROLLMENT_CAP_REACHED } from '@/consts/events';

import heroImage from '@/assets/images/Landing/hero.png';
import heroImageMobile from '@/assets/images/Landing/hero-mobile.png';

import iconMail from '@/assets/images/Landing/icon-mail.svg';
import iconPhone from '@/assets/images/Landing/icon-phone.svg';
import happyHeartsLogo from '@/assets/images/happy-hearts-logo.svg';
import FAQSection from './components/FAQSection';
import WhySection from './components/WhySection';
import CardsRow from './components/CardsRow';
import { redirect } from '@/navigation';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

// Was width:100vw, which is wider than the visible viewport whenever a
// vertical scrollbar is present (100vw includes the scrollbar gutter) -
// clamp to 100% of the parent (Container is already centered/full-width) so
// the hero never overflows horizontally.
const HeroSection = styled.div`
  width: 100%;
  height: 428px;
  max-width: var(--max-screen-width);

  @media (max-width: ${Dimensions.gridWidth - 1}px) {
    height: auto;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: min(${Dimensions.c10}px, 100vw);
  gap: ${Spacing.x5}px;
  box-sizing: border-box;

  @media (max-width: ${Dimensions.gridWidth - 1}px) {
    padding: 0 ${Spacing.x2}px;
  }
`;

const ContentSection = styled.div`
  display: flex;
  gap: ${Spacing.x5}px;
  align-items: flex-start;
  width: 100%;
  margin-top: ${Spacing.x5}px;

  @media (max-width: ${Dimensions.gridWidth - 1}px) {
    flex-direction: column;
    gap: ${Spacing.x4}px;
  }
`;

const CopyColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: ${Spacing.x4}px;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.x1}px;
  width: 100%;
`;

const CTAColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: ${Spacing.x2}px;
  align-items: center;
`;

const CTAButton = styled.button<{ $isLoading?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.x1}px;
  width: 100%;
  height: 56px;
  padding: 14px ${Spacing.x4}px;
  background-color: ${({ $isLoading }) =>
    $isLoading ? BorisScreener.btnPrimaryHover : BorisScreener.btnPrimary};
  color: ${Colors.background};
  font-family: Lato;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
  border: none;
  border-radius: 24px;
  cursor: ${({ $isLoading }) => ($isLoading ? 'default' : 'pointer')};

  &:hover {
    background-color: ${BorisScreener.btnPrimaryHover};
  }
`;

const LoadingSpinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid ${Colors.background};
  border-top-color: transparent;
  border-radius: 50%;
`;

const DisclaimerSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.x3}px;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const SupportBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.x1}px;
  background-color: ${Colors.backgroundAlt};
  border-radius: 12px;
  padding: ${Spacing.x2}px;
  width: 100%;
  box-sizing: border-box;
`;

const SupportLink = styled.a`
  display: flex;
  align-items: center;
  gap: ${Spacing.x1}px;
  color: ${Colors.primary};
  text-decoration: none;
  font-family: Lato;
  font-size: 16px;
  line-height: 24px;

  &:hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const SupportIcon = styled.img`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const Divider = styled.div`
  width: 100%;
  border-top: 1px solid ${Palette.natural100};
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.x1}px;
  align-items: center;
  text-align: center;
  width: 100%;
`;

const ReadySection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacing.x2}px;
  padding-top: ${Spacing.x5}px;
  width: 100%;
  max-width: ${Dimensions.c6}px;
`;

const ReadyCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.x1}px;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  padding-bottom: ${Spacing.x2}px;
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.x2}px;
  padding-top: ${Spacing.x5}px;
  padding-bottom: ${Spacing.x4}px;
`;

const PrivacyPolicyInlineLink = styled.button`
  font-family: Lato;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: ${Colors.primary};
  text-decoration: underline;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: inline;
`;

const MobileCenteredTitle = styled(Title)`
  @media (max-width: ${Dimensions.gridWidth - 1}px) {
    text-align: center;
  }
`;

const MobileCenteredText = styled(Text)`
  @media (max-width: ${Dimensions.gridWidth - 1}px) {
    text-align: center;
  }
`;

function getSupportEmailByPromoCode(promoCode: string) {
  return env.SUPPORT_EMAIL.replace('%@', promoCode);
}

const LandingStep: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trackView, track } = useAnalytics();
  const { navigateToNextStep } = useAppFlow(STEP_KEYS.LANDING);
  const { clearRegistrationData, setPromoCodeData } = useRegistration();
  const { showApiError } = useApiError();
  const [isCheckingPromoCode, setIsCheckingPromoCode] = useState(false);
  const [showTOSModal, setShowTOSModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const isMobile = useMediaQuery(`(max-width: ${Dimensions.gridWidth - 1}px)`);
  const promoCode = env.ACCESS_PILOT_PROMO_CODE;

  const checkPromoCodeAndNavigate = useCallback(
    async (shouldNavigate = false) => {
      try {
        setIsCheckingPromoCode(true);

        const response = await apiClient.promoCode(promoCode);

        // Store promo code data in context
        setPromoCodeData(response);

        if (response.capReached) {
          track({ event: ENROLLMENT_CAP_REACHED });
          navigate('/cap-reached');
          return;
        }

        setIsCheckingPromoCode(false);
        if (shouldNavigate) {
          navigateToNextStep();
        }
      } catch (error) {
        console.error('Error checking promo code:', error);
        setIsCheckingPromoCode(false);
        showApiError();
      }
    },
    [navigate, navigateToNextStep, showApiError, setPromoCodeData],
  );

  useEffect(() => {
    clearRegistrationData();
    trackView({ event: VIEW_WELCOME });
  }, [clearRegistrationData, trackView]);

  useEffect(() => {
    const { hash } = window.location;
    if (!hash) return;
    const id = hash.slice(1);
    requestAnimationFrame(() => {
      const element = document.getElementById(id);
      if (!element) return;
      const header = document.querySelector('header');
      const headerHeight = header ? header.getBoundingClientRect().height : 0;
      const top = element.getBoundingClientRect().top + window.scrollY - headerHeight;
      window.scrollTo({ top, behavior: 'smooth' });
    });
  }, []);

  const handleGetStarted = async () => {
    track({ event: START_WEBREG });
    await checkPromoCodeAndNavigate(true);
  };

  const handleNeedHelp = (type: 'email' | 'phone') => {
    track({
      event: NEED_HELP,
      properties: { source: 'welcome_access', type },
    });
    const email = getSupportEmailByPromoCode(promoCode);
    redirect(`mailto:${email}`);
  };

  return (
    <Container>
      <HeroSection>
        <HeroImage src={isMobile ? heroImageMobile : heroImage} alt={t('landing.title')} />
      </HeroSection>

      <ContentContainer>
        <ContentSection id="check-eligibility">
          <CopyColumn>
            <TitleGroup>
              <MobileCenteredTitle size="h3" bold>
                {t('landing.title')}
              </MobileCenteredTitle>
              <MobileCenteredText size="p1">{t('landing.subtitle')}</MobileCenteredText>
            </TitleGroup>
            <MobileCenteredText size="p1">{t('landing.description')}</MobileCenteredText>
          </CopyColumn>

          <CTAColumn>
            <CTAButton
              onClick={handleGetStarted}
              $isLoading={isCheckingPromoCode}
              aria-label={t('landing.cta')}
              disabled={isCheckingPromoCode}
            >
              {isCheckingPromoCode ? <LoadingSpinner /> : t('landing.cta')}
            </CTAButton>

            <DisclaimerSection>
              <Text size="p2" centered>
                {t('landing.disclaimer-cta')}
              </Text>

              <SupportBox id="help">
                <Text size="p2">{t('landing.support-box-title')}</Text>
                <SupportLink onClick={() => handleNeedHelp('email')}>
                  <SupportIcon src={iconMail} alt="" aria-hidden="true" />
                  {t('landing.support-email')}
                </SupportLink>
                <SupportLink
                  href={`tel:${env.SUPPORT_NUMBER}`}
                  onClick={() => handleNeedHelp('phone')}
                >
                  <SupportIcon src={iconPhone} alt="" aria-hidden="true" />
                  {env.SUPPORT_NUMBER}
                </SupportLink>
              </SupportBox>
            </DisclaimerSection>
          </CTAColumn>
        </ContentSection>

        <Divider />
        <SectionHeader id="what-you-get">
          <Title size="h3" centered>
            {t('landing.wyg-title')}
          </Title>
          <Text size="p1" centered>
            {t('landing.wyg-subtitle')}
          </Text>
        </SectionHeader>
        <CardsRow />

        <WhySection />

        <ReadySection id="get-started">
          <ReadyCopy>
            <Title size="h3" centered>
              {t('landing.ready-title')}
            </Title>
            <Text size="p1" centered>
              {t('landing.ready-subtitle')}
            </Text>
          </ReadyCopy>
          <CTAButton
            onClick={handleGetStarted}
            $isLoading={isCheckingPromoCode}
            aria-label={t('landing.cta')}
            disabled={isCheckingPromoCode}
          >
            {isCheckingPromoCode ? <LoadingSpinner /> : t('landing.cta')}
          </CTAButton>
          <Text size="p2" centered>
            {t('landing.ready-disclaimer')}
          </Text>
        </ReadySection>

        <FAQSection />

        <FooterSection>
          <Text size="p2">{t('landing.footer-disclaimer')}</Text>
          <Text size="p2">
            <Trans
              i18nKey="landing.footer-privacy-consent"
              components={{
                1: <PrivacyPolicyInlineLink onClick={() => setShowPrivacyModal(true)} />,
              }}
            />
          </Text>
          <img src={happyHeartsLogo} alt="Happy Hearts" width={128} height={32} />
        </FooterSection>
      </ContentContainer>

      {/* TOS Modal */}
      <TOSModal open={showTOSModal} onClose={() => setShowTOSModal(false)} />
      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal open={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />
    </Container>
  );
};

export { LandingStep };
